import { QueryFunctionContext } from "@tanstack/query-core";
import axios, { AxiosError } from "axios";
import { ChartDataDTO } from "../dto/ChartDTO";
import { ResearchDatesDTO } from "../dto/ResearchDatesDTO";
import ResearchDTO from "../dto/ResearchDTO";
import { ResearchAgentDTO, ResearchViewDTO } from "../dto/ResearchViewDTO";
import { useMutation, UseMutationOptions, useQuery } from "@tanstack/react-query";

export async function getResearchesByUser(): Promise<ResearchDTO[]> {
    const response = await axios.get(`/api/Researches/GetResearchesByUser`);
    return (response.data.result.researches as ResearchDTO[]);
}

export const useGetResearch = (
    researchId: number | null | undefined,
    onSuccess?: (data: ResearchDTO) => void,
    onError?: (error: AxiosError<string, any>) => void
    ) => {
    return useQuery<ResearchDTO, AxiosError<string, any>>({
        queryKey: ["Researches", "GetResearch", researchId],
        queryFn: async () => {
            return (await
                axios.get(`/api/Researches/GetResearch?id=${researchId}`))
                .data;
        },
        enabled: !!researchId,
        onSuccess: onSuccess,
        onError: onError
    });
};

export async function getCharts(context: QueryFunctionContext): Promise<ChartDataDTO[][]> {
    const response = await axios.get(`api/Researches/GetCharts?researchId=${context.queryKey[2] ?? ""}`);
    return (response.data.result.data as ChartDataDTO[][]);
}

export async function getResearchDates(context: QueryFunctionContext): Promise<ResearchDatesDTO> {
    const response = await axios.get(`api/Researches/GetResearchDate/${context.queryKey[2]}`);
    return (response.data.result as ResearchDatesDTO);
}

export async function editResearchStatus(params: { id: number, isOpen: boolean }) {
    const response = await axios.post(`/api/Researches/ChangeStatus?id=${params.id}&isOpen=${params.isOpen}`);
    return (response.data.result);
}

export interface IUsePostAddResearchMutateParams {
    research: ResearchDTO;
}

export const usePostAddResearchMutation = (options: UseMutationOptions<string, AxiosError, IUsePostAddResearchMutateParams>) => {
    return useMutation({
        ...options,
        mutationKey: ["postAddResearch"],
        mutationFn: async (params: IUsePostAddResearchMutateParams) => {
            return (await
                axios.post(`api/Researches/AddResearch`, params.research))
                .data;
        }
    })
}

export interface IUsePostEditResearchMutateParams {
    research: ResearchDTO;
}

export const usePostEditResearchMutation = (options: UseMutationOptions<string, AxiosError, IUsePostEditResearchMutateParams>) => {
    return useMutation({
        ...options,
        mutationKey: ["postEditResearch"],
        mutationFn: async (params: IUsePostEditResearchMutateParams) => {
            return (await
                axios.post(`api/Researches/EditResearch`, params.research))
                .data;
        }
    })
}

export const useGetResearchView = (
    researchId: number | null | undefined
) => {
    return useQuery<ResearchViewDTO, AxiosError>({
        queryKey: ["Researches", "GetResearch", researchId],
        queryFn: async () => {
            return (await
                axios.get(`/api/Researches/GetResearch?id=${researchId}`))
                .data;
        },
        enabled: !!researchId
    });
};

export const useGetAgents = (
    researchId: number
) => {
    return useQuery<ResearchAgentDTO[], AxiosError>({
        queryKey: ['GetAgents'],
        queryFn: async () => {
            return (await
                axios.get(`api/Researches/GetAgents?researchId=${researchId}`))
                .data
        },
        enabled: !!researchId
    })
}
